<template>
	<BaseCard
		:class="{ 'opacity-60': props.disabled }"
		:disabled="props.disabled"
	>
		<div class="flex items-center gap-4 p-5" @click="toggleSelected()">
			<Component :is="icon" class="text-label-secondary h-6 w-6 shrink-0" />
			<div class="min-w-0 flex-1">
				<div class="text-label-primary truncate text-sm font-medium uppercase">
					{{ title }}
				</div>
				<div class="text-label-secondary mt-1 text-sm">{{ desc }}</div>
			</div>
			<BaseToggle class="shrink-0" :modelValue="disabled ? false : selected" />
		</div>
		<slot />
	</BaseCard>
</template>
<script setup lang="ts">
import BaseCard from './BaseCard.vue'
import CrazyLgIcon from '@/assets/icons/crazy-lg.svg'
import PrivateIcon from '@/assets/icons/private.svg'
import LightningOutlineIcon from '@/assets/icons/lightning-outline.svg'
import BaseToggle from './BaseToggle.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FundingIcon from '@/assets/icons/funding.svg'

const { t } = useI18n({
	messages: {
		en: {
			fast: {
				title: 'Fast game',
				desc: '3x times the speed'
			},
			private: {
				title: 'Private game',
				desc: 'Publicly hide the game'
			},
			crazy: {
				title: 'Crazy Mode',
				desc: 'Unbox the least to win'
			},
			crazyJackpot: {
				title: 'Crazy Mode',
				desc: 'Smaller drop – higher chances'
			},
			funding: {
				title: 'Partial Funding',
				desc: 'Fund the battle for others'
			}
		},
		ru: {
			fast: {
				title: 'Быстрая игра',
				desc: 'x3 скорость'
			},
			private: {
				title: 'Приватная игра',
				desc: 'Скрыть игру от посторонних'
			},
			crazy: {
				title: 'Безумный режим',
				desc: 'Выиграть меньше всех для победы'
			},
			crazyJackpot: {
				title: 'Безумный режим',
				desc: 'Меньше дроп – выше шансы'
			},
			funding: {
				title: 'Оплата части',
				desc: 'Оплатите часть батла для других'
			}
		}
	}
})

const props = defineProps<{
	option: 'crazy' | 'private' | 'fast' | 'funding'
	gamemode?: 'classic' | 'shared' | 'jackpot'
	disabled?: boolean
}>()

const selected = defineModel<boolean>()

const toggleSelected = () => {
	if (props.disabled) {
		return
	}

	selected.value = !selected.value
}

const icon = computed(() => {
	if (props.option === 'private') {
		return PrivateIcon
	}
	if (props.option === 'fast') {
		return LightningOutlineIcon
	}
	if (props.option === 'funding') {
		return FundingIcon
	}

	return CrazyLgIcon
})

const title = computed(() => {
	if (props.option === 'private') {
		return t('private.title')
	}
	if (props.option === 'fast') {
		return t('fast.title')
	}
	if (props.option === 'funding') {
		return t('funding.title')
	}
	if (props.option === 'crazy' && props.gamemode === 'jackpot') {
		return t('crazyJackpot.title')
	}

	return t('crazy.title')
})

const desc = computed(() => {
	if (props.option === 'private') {
		return t('private.desc')
	}
	if (props.option === 'fast') {
		return t('fast.desc')
	}
	if (props.option === 'funding') {
		return t('funding.desc')
	}
	if (props.option === 'crazy' && props.gamemode === 'jackpot') {
		return t('crazyJackpot.desc')
	}

	return t('crazy.desc')
})
</script>
